import { useEffect, useRef, useCallback, useState } from 'react';
import { useSpeechToText, MicErrorCode } from './useSpeechToText';
import { useGrpcSpeechToText } from './useGrpcSpeechToText';
import { useSttProvider } from './useSttProvider';
import { useBotAudio } from './useBotAudio';
import { STT_PROVIDER } from '@constants/speechToText';
import type { VoicePermissionErrorType } from '@pages/chatbot/components/VoicePermissionModal';
import {
  actionTracker,
  ActionMacroFeature,
  ActionView,
  ActionType,
  ActionElement,
} from '../services/actionTracker';

export const VOICE_CAPTURE_MAX_DURATION_MS = 60_000;

/** Dialog của luồng voice (Figma: AI voice) */
export type MicDialogType = 'permission' | 'in-use' | 'unsupported' | 'not-found';

/** Đánh dấu user đã đồng ý ở dialog xin quyền (dùng khi trình duyệt không hỗ trợ Permissions API) */
const MIC_PERMISSION_ACK_KEY = 'tendoo_mic_permission_ack';

const hasMicPermissionAck = (): boolean => {
  try {
    return localStorage.getItem(MIC_PERMISSION_ACK_KEY) === '1';
  } catch {
    return false;
  }
};

const saveMicPermissionAck = () => {
  try {
    localStorage.setItem(MIC_PERMISSION_ACK_KEY, '1');
  } catch {
    // ignore
  }
};

const getMicPermissionState = async (): Promise<PermissionState | 'unknown'> => {
  try {
    if (!navigator.permissions?.query) return 'unknown';
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state;
  } catch {
    return 'unknown';
  }
};

type SttEngine = 'browser' | 'grpc';

const STT_ENGINE_LABEL: Record<SttEngine, string> = {
  grpc: 'gRPC Viettel',
  browser: 'Browser (Web Speech API)',
};

/** Lý do phải dùng STT trình duyệt dù chưa chắc là model BE chọn; null nếu đúng theo provider. */
const getSttFallbackReason = (provider: string, isLoaded: boolean, grpcSupported: boolean): string | null => {
  if (!isLoaded) return 'chưa lấy được provider từ API, dùng mặc định';
  if (provider === STT_PROVIDER.VIETTEL && !grpcSupported) {
    return 'trình duyệt không hỗ trợ gRPC (AudioWorklet/WebSocket)';
  }
  return null;
};

const MIC_ERROR_DIALOG: Partial<Record<MicErrorCode, MicDialogType>> = {
  'not-found': 'not-found',
  'in-use': 'in-use',
  unsupported: 'unsupported',
};

interface UseVoiceInteractionControllerProps {
  isThinking: boolean;
  isAudioEnabled: boolean;
  onVoiceInputSubmit: (text: string) => void;
  onChangeInput: (text: string) => void;
  resetInput: () => void;
  inputValue?: string;
  /** Access token để lấy provider STT (VIETTEL / DEFAULT_SDK) từ BE; token đổi → lấy lại. */
  token?: string | null;
  silenceTimeoutMs?: number;
  maxDurationMs?: number;
  onMicrophoneError?: (msg: string) => void;
  onVoiceErrorType?: (type: VoicePermissionErrorType) => void;
}

interface UseVoiceInteractionControllerReturn {
  listening: boolean;
  isBotSpeaking: boolean;
  isAudioPaused: boolean;
  isAudioPlayerVisible: boolean;
  audioElapsedSeconds: number;
  audioPlaybackRate: number;
  hasPendingAudio: boolean;
  isSTTSupported: boolean;
  voiceErrorModalType: VoicePermissionErrorType | null;
  playBotAudio: (audio: string) => void;
  stopBotSpeaking: () => void;
  toggleBotAudioPause: () => void;
  cycleBotAudioRate: () => void;
  handleMicrophoneClick: () => void;
  stopMicAndDiscard: () => void;
  pauseMicAndKeepText: () => void;
  cancelMicAndClearText: () => void;
  closeVoiceErrorModal: () => void;
  retryVoicePermission: () => void;
  micDialog: MicDialogType | null;
  closeMicDialog: () => void;
  confirmMicDialog: () => void;
}

export const useVoiceInteractionController = ({
  isThinking,
  isAudioEnabled,
  onVoiceInputSubmit,
  onChangeInput,
  resetInput,
  inputValue = '',
  token,
  silenceTimeoutMs = 5000,
  maxDurationMs = VOICE_CAPTURE_MAX_DURATION_MS,
  onMicrophoneError,
  onVoiceErrorType,
}: UseVoiceInteractionControllerProps): UseVoiceInteractionControllerReturn => {
  const {
    play: playBotAudio,
    stop: stopBotSpeaking,
    togglePause: toggleBotAudioPause,
    cyclePlaybackRate: cycleBotAudioRate,
    isSpeaking: isBotSpeaking,
    isPaused: isAudioPaused,
    isPlayerVisible: isAudioPlayerVisible,
    elapsedSeconds: audioElapsedSeconds,
    playbackRate: audioPlaybackRate,
    hasPendingAudio,
  } = useBotAudio({ isEnabled: isAudioEnabled });

  const [voiceErrorModalType, setVoiceErrorModalType] = useState<VoicePermissionErrorType | null>(null);
  const micPausedForThinkingRef = useRef(false);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceAutoSentRef = useRef(false);

  const inputValueRef = useRef(inputValue);
  const currentInputRef = useRef('');
  const prefixInputRef = useRef('');

  const [micDialog, setMicDialog] = useState<MicDialogType | null>(null);
  const micStartTimeRef = useRef<number>(0);

  useEffect(() => { inputValueRef.current = inputValue; }, [inputValue]);

  const handleSTTResult = useCallback((text: string) => {
    const prefix = prefixInputRef.current;
    const fullText = prefix ? `${prefix} ${text}` : text;
    currentInputRef.current = fullText;
    onChangeInput(fullText);
  }, [onChangeInput]);

  const handleSilenceTimeout = useCallback(() => {
    micPausedForThinkingRef.current = false;
    const currentInput = currentInputRef.current;
    if (currentInput && currentInput.trim()) {
      silenceAutoSentRef.current = true;
      onVoiceInputSubmit(currentInput);
      resetInput();
      currentInputRef.current = '';
      prefixInputRef.current = '';
    }
  }, [onVoiceInputSubmit, resetInput]);

  const handleSTTError = useCallback((msg: string, code?: MicErrorCode) => {
    if (!msg || msg === 'aborted' || msg === 'abort') return;
    if (code && MIC_ERROR_DIALOG[code]) {
      setMicDialog(MIC_ERROR_DIALOG[code]!);
    }
    onMicrophoneError?.(msg);
  }, [onMicrophoneError]);

  const handleSTTErrorType = useCallback((type: VoicePermissionErrorType) => {
    setVoiceErrorModalType(type);
    onVoiceErrorType?.(type);
  }, [onVoiceErrorType]);

  // BE quyết định model STT theo app_config của thiết bị (deviceType=web):
  // VIETTEL → gRPC Viettel, DEFAULT_SDK (hoặc chưa lấy được) → STT của trình duyệt như trước.
  const { provider: sttProvider, isLoaded: isSttProviderLoaded, refresh: refreshSttProvider } = useSttProvider(token);

  const browserSTT = useSpeechToText({
    onResult: handleSTTResult,
    silenceTimeoutMs,
    maxDurationMs,
    onSilenceTimeout: handleSilenceTimeout,
    onError: handleSTTError,
    onErrorType: handleSTTErrorType,
  });

  // gRPC không dùng silenceTimeoutMs/maxDurationMs: timeout hoàn toàn do server (NO_SPEECH_TIMEOUT)
  const grpcSTT = useGrpcSpeechToText({
    onResult: handleSTTResult,
    onSilenceTimeout: handleSilenceTimeout,
    onError: handleSTTError,
    onErrorType: handleSTTErrorType,
  });

  // Provider VIETTEL nhưng trình duyệt không hỗ trợ AudioWorklet/WebSocket → quay về STT trình duyệt
  const shouldUseGrpc = sttProvider === STT_PROVIDER.VIETTEL && grpcSTT.isSupported;
  const shouldUseGrpcRef = useRef(shouldUseGrpc);
  shouldUseGrpcRef.current = shouldUseGrpc;
  // Engine của lần bật mic hiện tại: stop/discard luôn gọi đúng engine đã start dù provider đổi giữa chừng
  const activeEngineRef = useRef<SttEngine>('browser');
  const isSttProviderLoadedRef = useRef(isSttProviderLoaded);
  isSttProviderLoadedRef.current = isSttProviderLoaded;
  // Trạng thái chọn engine mới nhất, chỉ dùng để log lý do chọn/fallback khi bật mic
  const sttSelectionRef = useRef({ provider: sttProvider, isLoaded: isSttProviderLoaded, grpcSupported: grpcSTT.isSupported });
  sttSelectionRef.current = { provider: sttProvider, isLoaded: isSttProviderLoaded, grpcSupported: grpcSTT.isSupported };

  const { start: startBrowserSTT, stop: stopBrowserSTT, stopAndDiscard: stopAndDiscardBrowserSTT } = browserSTT;
  const { start: startGrpcSTT, stop: stopGrpcSTT, stopAndDiscard: stopAndDiscardGrpcSTT } = grpcSTT;

  const startSTT = useCallback(() => {
    activeEngineRef.current = shouldUseGrpcRef.current ? 'grpc' : 'browser';
    const { provider, isLoaded, grpcSupported } = sttSelectionRef.current;
    const fallbackReason = getSttFallbackReason(provider, isLoaded, grpcSupported);
    console.log(
      `[STT] Mic start → engine=${STT_ENGINE_LABEL[activeEngineRef.current]} | provider=${provider} | loaded=${isLoaded}` +
        (fallbackReason ? ` | fallback: ${fallbackReason}` : ''),
    );
    if (activeEngineRef.current === 'grpc') {
      startGrpcSTT();
    } else {
      startBrowserSTT();
    }
  }, [startGrpcSTT, startBrowserSTT]);

  const stopSTT = useCallback(() => {
    if (activeEngineRef.current === 'grpc') {
      stopGrpcSTT();
    } else {
      stopBrowserSTT();
    }
  }, [stopGrpcSTT, stopBrowserSTT]);

  const stopAndDiscardSTT = useCallback(() => {
    if (activeEngineRef.current === 'grpc') {
      stopAndDiscardGrpcSTT();
    } else {
      stopAndDiscardBrowserSTT();
    }
  }, [stopAndDiscardGrpcSTT, stopAndDiscardBrowserSTT]);

  const listening = browserSTT.isListening || grpcSTT.isListening;
  const isSTTSupported = shouldUseGrpc ? grpcSTT.isSupported : browserSTT.isSupported;

  const listeningRef = useRef(listening);
  useEffect(() => { listeningRef.current = listening; }, [listening]);

  useEffect(() => {
    if (isBotSpeaking && listening) stopSTT();
  }, [isBotSpeaking, listening, stopSTT]);

  const pauseMicAndKeepText = useCallback(() => {
    micPausedForThinkingRef.current = false;
    silenceAutoSentRef.current = true;
    if (micStartTimeRef.current > 0) {
      const durationMs = Date.now() - micStartTimeRef.current;
      micStartTimeRef.current = 0;
      actionTracker.track({
        view: ActionView.CHATBOT_AI_ASSISTANT,
        feature: ActionMacroFeature.CHATBOT_V2,
        action: ActionType.RELEASE,
        element: ActionElement.BTN_MIC,
        durationMs,
        extraContext: {
          voice_input_duration_s: Number((durationMs / 1000).toFixed(2)),
        },
      });
    }
    stopSTT();
  }, [stopSTT]);

  // Rule 3: Dừng voice khi thao tác khác (ngoại trừ cuộn và icon cuộn xuống dưới)
  useEffect(() => {
    if (!listening) return;
    const isExempt = (target: HTMLElement | null): boolean => {
      if (!target) return true;
      if (target.closest('.conversation-scroll-down-btn')) return true;
      return Boolean(target.closest('.input-message-card, .voice-permission-modal-card'));
    };
    const handlePointer = (e: PointerEvent) => {
      if (!isExempt(e.target as HTMLElement | null)) pauseMicAndKeepText();
    };
    document.addEventListener('pointerdown', handlePointer, true);
    return () => document.removeEventListener('pointerdown', handlePointer, true);
  }, [listening, pauseMicAndKeepText]);

  const cancelMicAndClearText = useCallback(() => {
    silenceAutoSentRef.current = true;
    micPausedForThinkingRef.current = false;
    if (micStartTimeRef.current > 0) {
      const durationMs = Date.now() - micStartTimeRef.current;
      micStartTimeRef.current = 0;
      actionTracker.track({
        view: ActionView.CHATBOT_AI_ASSISTANT,
        feature: ActionMacroFeature.CHATBOT_V2,
        action: ActionType.RELEASE,
        element: ActionElement.BTN_MIC,
        durationMs,
        errorCode: 'cancelled',
        extraContext: {
          voice_input_duration_s: Number((durationMs / 1000).toFixed(2)),
        },
      });
    }
    stopAndDiscardSTT();
    currentInputRef.current = '';
    prefixInputRef.current = '';
    onChangeInput('');
    resetInput();
  }, [stopAndDiscardSTT, onChangeInput, resetInput]);

  const handleMicrophoneClick = useCallback(() => {
    if (listening) {
      pauseMicAndKeepText();
      return;
    }
    if (isBotSpeaking) stopBotSpeaking();
    micPausedForThinkingRef.current = false;
    silenceAutoSentRef.current = false;
    const currentVal = (inputValueRef.current || '').trim();
    prefixInputRef.current = currentVal;
    currentInputRef.current = currentVal;

    micStartTimeRef.current = Date.now();
    actionTracker.track({
      view: ActionView.CHATBOT_AI_ASSISTANT,
      feature: ActionMacroFeature.CHATBOT_V2,
      action: ActionType.TAP,
      element: ActionElement.BTN_MIC,
    });
    // Chưa lấy được provider (token đến muộn / lỗi mạng) → thử lại nền cho lần bấm sau, lần này dùng mặc định
    if (!isSttProviderLoadedRef.current) void refreshSttProvider();
    startSTT();
  }, [listening, isBotSpeaking, pauseMicAndKeepText, stopBotSpeaking, startSTT, refreshSttProvider]);

  const closeVoiceErrorModal = useCallback(() => {
    setVoiceErrorModalType(null);
  }, []);

  const retryVoicePermission = useCallback(() => {
    setVoiceErrorModalType(null);
    handleMicrophoneClick();
  }, [handleMicrophoneClick]);

  const closeMicDialog = useCallback(() => {
    setMicDialog(null);
  }, []);

  const confirmMicDialog = useCallback(() => {
    setMicDialog(null);
    handleMicrophoneClick();
  }, [handleMicrophoneClick]);

  return {
    listening,
    isBotSpeaking,
    isAudioPaused,
    isAudioPlayerVisible,
    audioElapsedSeconds,
    audioPlaybackRate,
    hasPendingAudio,
    isSTTSupported,
    voiceErrorModalType,
    playBotAudio,
    stopBotSpeaking,
    toggleBotAudioPause,
    cycleBotAudioRate,
    handleMicrophoneClick,
    stopMicAndDiscard: cancelMicAndClearText,
    pauseMicAndKeepText,
    cancelMicAndClearText,
    closeVoiceErrorModal,
    retryVoicePermission,
    micDialog,
    closeMicDialog,
    confirmMicDialog,
  };
};

export default useVoiceInteractionController;
